import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * generatePdfFromElement:
 * - elem: HTMLElement to render
 * - filename: desired file name
 * - options: optional { format: 'a4'|'letter'|'thermal58', orientation: 'portrait'|'landscape' }
 */
export async function generatePdfFromElement(elem: HTMLElement, filename = "document.pdf", options?: { format?: string, orientation?: "portrait" | "landscape" }) {
  if (!elem) throw new Error("Element not found");

  const format = options?.format ?? "a4";
  const orientation = options?.orientation ?? "portrait";

  // PDF page size in mm for A4: 210x297. We'll use jsPDF units = mm.
  const pdf = new jsPDF({ unit: "mm", format, orientation });

  // Render element to canvas with high scale for better quality
  const scale = 2; // increase for better resolution (tradeoff file size)
  const canvas = await html2canvas(elem, {
    scale,
    useCORS: true,
    logging: false,
    windowWidth: elem.scrollWidth,
    windowHeight: elem.scrollHeight,
  });

  const imgData = canvas.toDataURL("image PNG", 1.0);

  // Calculate image dimensions in mm for the PDF
  const imgProps = {
    widthPx: canvas.width,
    heightPx: canvas.height,
  };

  // jsPDF page size in mm
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // convert px to mm: px * 25.4 / dpi. html2canvas uses 96dpi by default
  const pxToMm = (px: number) => (px * 25.4) / (96 * scale); // divide by scale because we rendered with scale

  const imgWidthMm = pxToMm(imgProps.widthPx);
  const imgHeightMm = pxToMm(imgProps.heightPx);

  // If image height fits in one page, just add it centered
  if (imgHeightMm <= pageHeight) {
    const x = (pageWidth - imgWidthMm) / 2;
    pdf.addImage(imgData, "PNG", x, 10, imgWidthMm, imgHeightMm); // leave small top margin 10mm
  } else {
    // multi-page: slice image by pageHeight
    // We'll calculate how many vertical pages are needed and draw each piece
    let remainingHeightPx = imgProps.heightPx;
    let offsetYpx = 0;
    const pageHeightPx = Math.round((pageHeight * 96 * scale) / 25.4); // convert mm back to px (for cropping)
    while (remainingHeightPx > 0) {
      // create a canvas for the single page slice
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.min(pageHeightPx, remainingHeightPx);
      const ctx = pageCanvas.getContext("2d")!;
      // draw slice from original canvas
      ctx.drawImage(
        canvas,
        0,
        offsetYpx,
        canvas.width,
        pageCanvas.height,
        0,
        0,
        canvas.width,
        pageCanvas.height
      );
      const pageData = pageCanvas.toDataURL("image/png");
      const pageImgHeightMm = pxToMm(pageCanvas.height);

      const x = (pageWidth - imgWidthMm) / 2;
      // if not first page -> add new page
      if (offsetYpx > 0) pdf.addPage();
      pdf.addImage(pageData, "PNG", x, 10, imgWidthMm, pageImgHeightMm);

      // next slice
      remainingHeightPx -= pageCanvas.height;
      offsetYpx += pageCanvas.height;
    }
  }

  // Save file
  pdf.save(filename);
}
